// packages/character-render/src/proceduralHumanoid.ts
//
// Procedural humanoid builder: role-based palettes and silhouettes built from
// a few scaled boxes per limb (legs side by side, two-tone boots/trousers,
// sleeves with hands, a face, hats and costume pieces). Output is plain
// `HumanoidPart[]`, so it renders through <HumanoidMesh> in 1–3 draw calls.
//
// Deterministic: the same (role, seed) always yields the same figure.
//
// The spec types, the role templates, the hats and the box helpers live in
// sibling modules; the public names are re-exported here so existing imports
// of './proceduralHumanoid' keep working.
import { METAL, type V3, box, stack } from './humanoidBoxes';
import { pushHat } from './humanoidHats';
import type { HumanoidPart } from './humanoidRig';
import { type HumanoidRole, humanoidSpecForRole } from './humanoidRoles';
import type { ProceduralHumanoidSpec } from './humanoidSpec';

export { HUMANOID_ROLES, hashSeed, humanoidSpecForRole } from './humanoidRoles';
export type { HumanoidRole } from './humanoidRoles';
export type {
  BuildStyle,
  CoatStyle,
  HatStyle,
  HumanoidPalette,
  ProceduralHumanoidSpec,
} from './humanoidSpec';

const EYE = '#1b1616';

/**
 * Build a humanoid from a spec. Proportions are authored for a 1-unit figure
 * and scaled to `height`. The figure faces +Z with its feet at y = 0.
 */
export function buildProceduralHumanoid(spec: ProceduralHumanoidSpec): HumanoidPart[] {
  const H = spec.height ?? 1.9;
  const p = spec.palette;
  const build = spec.build ?? 'regular';
  const torsoW = build === 'slim' ? 0.3 : build === 'stout' ? 0.38 : 0.34;
  const torsoD = build === 'stout' ? 0.22 : 0.19;
  const legW = build === 'stout' ? 0.13 : 0.12;
  const legX = build === 'stout' ? 0.078 : 0.068;
  const sleeve = spec.coat && spec.coat !== 'none' ? (p.coat ?? p.shirt) : p.shirt;
  const upper = spec.coat && spec.coat !== 'none' ? (p.coat ?? p.shirt) : p.shirt;
  const alt = spec.stripes ? (p.shirtAlt ?? p.shirt) : upper;

  const parts: HumanoidPart[] = [];
  const sv = (v: V3): V3 => [v[0] * H, v[1] * H, v[2] * H];

  // Legs — side by side, boots then trousers.
  for (const side of [-1, 1] as const) {
    parts.push(
      stack(
        side < 0 ? 'left_leg' : 'right_leg',
        [p.boots, p.pants, p.pants, p.pants],
        sv([legW, 0.11, 0.14]),
        sv([side * legX, 0, 0]),
        {
          roughness: 0.88,
        },
      ),
    );
  }

  // Torso — belt at the waist, then shirt/coat (optionally striped).
  parts.push(
    stack('torso', [p.belt, upper, alt, upper], sv([torsoW, 0.08, torsoD]), sv([0, 0.44, 0]), {
      roughness: 0.85,
    }),
  );

  // Open coat shows the shirt down the front.
  if (spec.coat && spec.coat !== 'none') {
    parts.push(
      box('misc', p.shirt, sv([0.11, 0.25, 0.02]), sv([0, 0.63, torsoD / 2 + 0.006]), {
        bone: 'body',
      }),
    );
    if (spec.coat === 'long' || spec.coat === 'robe') {
      const skirtH = spec.coat === 'robe' ? 0.36 : 0.2;
      parts.push(
        box(
          'misc',
          p.coat ?? p.shirt,
          sv([torsoW + 0.03, skirtH, torsoD + 0.03]),
          sv([0, 0.46 - skirtH / 2, 0]),
          {
            bone: 'body',
            roughness: 0.9,
          },
        ),
      );
    }
  }

  if (build === 'stout') {
    parts.push(
      box('misc', upper, sv([torsoW - 0.05, 0.15, 0.06]), sv([0, 0.55, torsoD / 2 + 0.02]), {
        bone: 'body',
      }),
    );
  }
  if (spec.apron) {
    parts.push(
      box(
        'misc',
        p.accent,
        sv([0.25, 0.3, 0.02]),
        sv([0, 0.55, torsoD / 2 + (build === 'stout' ? 0.055 : 0.012)]),
        { bone: 'body', roughness: 0.95 },
      ),
    );
  }
  if (spec.sash) {
    parts.push(
      box('misc', p.accent, sv([torsoW + 0.07, 0.045, torsoD + 0.025]), sv([0, 0.62, 0]), {
        bone: 'body',
        rotation: [0, 0, 0.62],
      }),
    );
  }
  if (spec.epaulettes) {
    for (const side of [-1, 1] as const) {
      parts.push(
        box(
          'misc',
          p.accent,
          sv([0.12, 0.035, 0.14]),
          sv([side * (torsoW / 2 + 0.035), 0.775, 0]),
          { bone: 'body', ...METAL },
        ),
      );
    }
  }
  if (spec.cutlass) {
    parts.push(
      box('misc', '#cfd4da', sv([0.025, 0.34, 0.05]), sv([-(torsoW / 2 + 0.03), 0.33, 0.05]), {
        bone: 'body',
        ...METAL,
      }),
    );
    parts.push(
      box('misc', p.accent, sv([0.07, 0.03, 0.07]), sv([-(torsoW / 2 + 0.03), 0.515, 0.05]), {
        bone: 'body',
        ...METAL,
      }),
    );
  }

  // Arms — hand, then two sleeve segments, hanging from the shoulder.
  const armX = torsoW / 2 + 0.056;
  for (const side of [-1, 1] as const) {
    parts.push(
      stack(
        side < 0 ? 'left_arm' : 'right_arm',
        [p.skin, sleeve, sleeve],
        sv([0.1, 0.107, 0.11]),
        sv([side * armX, 0.445, 0]),
        {
          roughness: 0.8,
        },
      ),
    );
  }

  // Head + face.
  parts.push(box('head', p.skin, sv([0.24, 0.235, 0.225]), sv([0, 0.8875, 0]), { roughness: 0.6 }));
  parts.push(box('head', p.skin, sv([0.1, 0.03, 0.1]), sv([0, 0.77, 0]), { roughness: 0.6 })); // neck
  for (const side of [-1, 1] as const) {
    parts.push(
      box('misc', EYE, sv([0.04, 0.035, 0.02]), sv([side * 0.055, 0.905, 0.114]), {
        bone: 'head',
        roughness: 0.3,
      }),
    );
  }
  parts.push(
    box('misc', p.skin, sv([0.035, 0.05, 0.035]), sv([0, 0.865, 0.122]), {
      bone: 'head',
      roughness: 0.6,
    }),
  ); // nose

  // Hair (under any hat) and beard.
  parts.push(
    box('misc', p.hair, sv([0.25, 0.05, 0.235]), sv([0, 1.0, -0.004]), {
      bone: 'head',
      roughness: 0.9,
    }),
  );
  parts.push(
    box('misc', p.hair, sv([0.25, 0.15, 0.04]), sv([0, 0.925, -0.112]), {
      bone: 'head',
      roughness: 0.9,
    }),
  );
  if (spec.longHair) {
    parts.push(
      box('misc', p.hair, sv([0.25, 0.22, 0.05]), sv([0, 0.8, -0.13]), {
        bone: 'head',
        roughness: 0.9,
      }),
    );
  }
  if (spec.beard) {
    parts.push(
      box('misc', p.hair, sv([0.2, 0.085, 0.045]), sv([0, 0.8, 0.108]), {
        bone: 'head',
        roughness: 0.95,
      }),
    );
  }

  pushHat(parts, spec.hat ?? 'none', p, sv);

  return parts;
}

/** Parts for a role-styled humanoid; stable for a given `(role, seedKey)`. */
export function humanoidPartsForRole(
  role: HumanoidRole,
  seedKey: string,
  height?: number,
): HumanoidPart[] {
  const spec = humanoidSpecForRole(role, seedKey);
  return buildProceduralHumanoid(height ? { ...spec, height } : spec);
}
